(function (root) {
  'use strict';
  function isMini(variant) {
    const options = variant.options || [variant.option1, variant.option2, variant.option3];
    const sizes = options.filter(Boolean).map(value => String(value).toLowerCase().replace(/\s/g, ''));
    const sku = '-' + String(variant.sku || '').toUpperCase().replace(/_/g, '-') + '-';
    return sizes.includes('10ml') || (/-(10|10ML)-/.test(sku) && !/-(50|50ML)-/.test(sku));
  }
  function filterProduct(product, enabled) {
    const tags = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',').map(value => value.trim());
    if (!enabled || !tags.some(tag => String(tag).trim().toLowerCase() === 'perfume')) return product;
    const variants = (product.variants || []).filter(variant => !isMini(variant));
    const prices = variants.map(variant => Number(variant.price));
    return Object.assign({}, product, {
      variants,
      available: variants.some(variant => variant.available),
      price: prices.length ? Math.min(...prices) : 0,
      price_min: prices.length ? Math.min(...prices) : 0,
      price_max: prices.length ? Math.max(...prices) : 0,
      price_varies: new Set(prices).size > 1,
      options: (product.options || []).map((option, index) => typeof option === 'string' ? option : Object.assign({}, option, { values: option.values.filter(value => variants.some(variant => (variant.options || [variant.option1, variant.option2, variant.option3])[index] === value)) }))
    });
  }
  function bundleAllowed(state) {
    return !!(state.marketEligible && state.configured && state.base?.available && state.bundle?.available && Number(state.bundle.price) === Number(state.base.price) + 1500);
  }
  const api = { isMini, filterProduct, bundleAllowed };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!root.document) return;
  const configNode = root.document.querySelector('[data-aerre-bundle-config]');
  const config = configNode ? JSON.parse(configNode.textContent) : { enabled: false, strings: {} };
  api.enabled = config.enabled;
  api.controllers = new Map();
  api.filterProduct = product => filterProduct(product, config.enabled);
  api.beforeAdd = async form => {
    const controller = api.controllers.get(form.getAttribute('id'));
    if (controller) { await controller.readyPromise; await controller.validateSelection(); }
    const allowed = form.getAttribute('data-aerre-allowed-variants');
    const chosen = String(form.elements.namedItem('id')?.value || '');
    if (allowed !== null && !allowed.split(',').filter(Boolean).includes(chosen) && !(controller && chosen === String(controller.state.bundle?.id) && bundleAllowed(controller.state))) {
      throw new Error(config.strings.unavailable);
    }
    root.AerreTierEligibility?.syncForm(form);
  };
  root.AerreBundles = api;
  class AerreBundleSelector extends HTMLElement {
    async connectedCallback() {
      if (this.ready) return;
      this.state = JSON.parse(this.querySelector('[data-bundle-state]').textContent);
      this.form = root.document.getElementById(this.getAttribute('form-id'));
      if (!this.form || !this.state.base) return;
      this.ready = true;
      this.status = this.querySelector('[role="status"]');
      this.onChange = event => {
        if (event.target.matches('input[type="radio"]')) this.choose(event.target.hasAttribute('data-bundle-option'));
      };
      this.addEventListener('change', this.onChange);
      this.onGalleryReady = () => { if (this.master) this.choose(this.usingBundle, false); };
      this.form.addEventListener('aerre:gallery-ready', this.onGalleryReady);
      this.readyPromise = Promise.all([root.customElements.whenDefined('product-form'), root.customElements.whenDefined('product-meta')]);
      await this.readyPromise;
      if (!this.isConnected) return;
      this.master = this.form.elements.namedItem('id');
      api.controllers.set(this.form.getAttribute('id'), this);
      this.master.value = this.state.base.id;
      const bundleInput = this.querySelector('[data-bundle-option]');
      if (bundleInput) bundleInput.disabled = !bundleAllowed(this.state);
      const location = new URL(root.location.href);
      const requestedBundle = location.searchParams.get('aerre_bundle') === '1' && !this.closest('.drawer, .popover');
      this.choose(requestedBundle && bundleAllowed(this.state), false);
    }
    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.form?.removeEventListener('aerre:gallery-ready', this.onGalleryReady);
      if (api.controllers.get(this.getAttribute('form-id')) === this) api.controllers.delete(this.getAttribute('form-id'));
      this.ready = false;
    }
    choose(wantsBundle, updateUrl = true) {
      if (!this.master) return;
      const useBundle = wantsBundle && bundleAllowed(this.state);
      const variant = useBundle ? this.state.bundle : this.state.base;
      this.usingBundle = useBundle;
      this.master.value = variant.id;
      this.master.disabled = false;
      this.querySelectorAll('input[type="radio"]').forEach(input => input.checked = String(input.value) === String(variant.id));
      this.master.dispatchEvent(new Event('change', { bubbles: true }));
      const baseMedia = this.state.baseMedia || this.state.base.featured_media;
      const featuredMedia = useBundle ? this.state.bundleMedia || baseMedia : baseMedia;
      this.form.dispatchEvent(new CustomEvent('variant:changed', { bubbles: true, detail: { variant: Object.assign({}, variant, { featured_media: featuredMedia, options: this.state.base.options }), aerreBundle: true, bundleSelected: useBundle, sourceVariantId: this.state.base.id } }));
      if (!this.closest('.drawer, .popover') && (updateUrl || new URL(root.location.href).searchParams.has('variant'))) {
        const url = new URL(root.location.href);
        url.searchParams.set('variant', this.state.base.id);
        if (useBundle) url.searchParams.set('aerre_bundle', '1'); else url.searchParams.delete('aerre_bundle');
        root.history.replaceState(root.history.state, '', url);
      }
      this.status.hidden = true;
    }
    async validateSelection() {
      if (!this.usingBundle) {
        if (String(this.master.value) !== String(this.state.base.id)) throw new Error(config.strings.unavailable);
        return;
      }
      const prefix = (root.Shopify?.routes?.root || '/').replace(/\/$/, '');
      const fetchProduct = async handle => {
        const response = await fetch(`${prefix}/products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error(config.strings.unavailable);
        return response.json();
      };
      try {
        const displayedBasePrice = this.state.base.price;
        const displayedBundlePrice = this.state.bundle.price;
        const [baseProduct, bundleProduct] = await Promise.all([fetchProduct(this.state.baseHandle), fetchProduct(this.state.bundleHandle)]);
        const nextState = Object.assign({}, this.state, {
          base: baseProduct.variants.find(variant => String(variant.id) === String(this.state.base.id)),
          bundle: bundleProduct.variants.find(variant => String(variant.id) === String(this.state.bundle.id))
        });
        if (!bundleAllowed(nextState) || nextState.base.price !== displayedBasePrice || nextState.bundle.price !== displayedBundlePrice) throw new Error(config.strings.unavailable);
        if (String(this.master.value) !== String(nextState.bundle.id)) throw new Error(config.strings.unavailable);
        this.state = nextState;
      } catch (error) {
        this.status.textContent = config.strings.unavailable;
        this.status.hidden = false;
        throw error;
      }
    }
  }
  if (!root.customElements.get('aerre-bundle-selector')) root.customElements.define('aerre-bundle-selector', AerreBundleSelector);
  root.document.addEventListener('submit', async event => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-aerre-allowed-variants')) return;
    const native = root.themeVariables?.settings?.cartType === 'page' || root.themeVariables?.settings?.pageType === 'cart';
    if (!native || form.dataset.aerreBundleValidated === 'true') { delete form.dataset.aerreBundleValidated; return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    try {
      if (root.aerreThresholdOffer) await root.aerreThresholdOffer.beforeCartWrite();
      await api.beforeAdd(form);
      form.dataset.aerreBundleValidated = 'true';
      if (event.submitter?.form === form) form.requestSubmit(event.submitter); else form.requestSubmit();
    } catch (error) {
      form.dispatchEvent(new CustomEvent('cart-notification:show', { bubbles: true, detail: { status: 'error', error: error.message } }));
    }
  }, true);
})(typeof window === 'undefined' ? globalThis : window);
